import { makeAutoObservable, runInAction } from "mobx";
import {
  adminGetServiceCenterLegalData,
  adminUpdateServiceCenterLegalData,
  adminGetServiceCenterContractorData,
  adminUpdateServiceCenterContractorData,
} from "@pro-sto/api-client";
import { toastStore } from "../../ToastStore";
import type {
  DadataSuggestion,
  LegalFormData,
  RawContractorData,
  RawLegalData,
} from "./legal-types";
import { hasData } from "./legal-types";
import { LegalFormStore } from "./sub-stores/LegalFormStore";
import { LegalStatusStore } from "./sub-stores/LegalStatusStore";
import { LegalDrawerStore } from "./sub-stores/LegalDrawerStore";
import { DadataStore } from "./sub-stores/DadataStore";
import { LegalSubmitStore } from "./sub-stores/LegalSubmitStore";
import { ContractSubStore } from "./sub-stores/ContractSubStore";
import { TBankSubStore } from "./sub-stores/TBankSubStore";

// ─── Re-export shared types (обратная совместимость импортов в компонентах) ───
export type { DadataSuggestion, LegalFormData } from "./legal-types";

// ─── Координатор: оркестрирует все суб-сторы ─────────────────────────────────

export class LegalDataSubStore {
  readonly status: LegalStatusStore;
  readonly form: LegalFormStore;
  readonly dadata: DadataStore;
  readonly submit: LegalSubmitStore;
  readonly drawer: LegalDrawerStore;
  readonly contract: ContractSubStore;
  readonly tbank: TBankSubStore;

  constructor(private getUuid: () => string) {
    this.status = new LegalStatusStore();
    this.form = new LegalFormStore();
    this.dadata = new DadataStore();
    this.submit = new LegalSubmitStore();
    this.drawer = new LegalDrawerStore();
    this.contract = new ContractSubStore();
    this.tbank = new TBankSubStore();
    makeAutoObservable(this, {
      status: false,
      form: false,
      dadata: false,
      submit: false,
      drawer: false,
      contract: false,
      tbank: false,
    });
  }

  // ── Загрузка статуса юр. данных ───────────────────────────────────────────

  checkLegalData = async () => {
    const uuid = this.getUuid();
    if (!uuid) return;
    this.status.isLoadingCheck = true;
    try {
      const [legalRaw, contractorRaw] = await Promise.all([
        adminGetServiceCenterLegalData({ serviceCenterUuid: uuid }).catch(
          () => null,
        ),
        adminGetServiceCenterContractorData({ serviceCenterUuid: uuid }).catch(
          () => null,
        ),
      ]);
      runInAction(() => {
        if (hasData(legalRaw)) {
          const legal = legalRaw as RawLegalData;
          // Приоритет: поле isLegalDataFilled из API, если оно есть
          const filled = legal.isLegalDataFilled ?? true;
          this.status.hasLegalData = true;
          this.status.isLegalDataFilled = filled;
          this.status.legalType = "legal";
          this.status.rawLegalData = legal;
          this.status.rawContractorData = null;
          this.form.prefillFromLegal(legal);
        } else if (hasData(contractorRaw)) {
          const contractor = contractorRaw as RawContractorData;
          this.status.hasLegalData = true;
          this.status.legalType = "selfemployed";
          this.status.rawContractorData = contractor;
          this.status.rawLegalData = null;
          this.form.prefillFromContractor(contractor);
        } else {
          this.status.hasLegalData = false;
          this.status.legalType = null;
        }
        this.status.isLoadingCheck = false;
      });
      // Параллельно загружаем ссылку на договор (если уже сгенерирован)
      void this.contract.loadContractLink(uuid);
    } catch (e) {
      runInAction(() => {
        this.status.isLoadingCheck = false;
      });
    }
  };

  // ── Регистрация в Т-Банке ─────────────────────────────────────────────────

  registerInTBank = () => {
    const uuid = this.getUuid();
    void this.tbank.register(uuid, () => {
      runInAction(() => {
        this.status.tbankRegisteredAt = new Date().toISOString();
      });
    });
  };

  // ── Открытие / закрытие дровера ───────────────────────────────────────────

  openDrawer = () => {
    if (this.status.hasLegalData && this.status.legalType) {
      // Данные уже есть — предзаполняем тип, но всегда начинаем с шага 0
      this.drawer.legalTypeSelected = this.status.legalType;
      const detected = this._detectCompletedSteps();
      this.drawer.setInitialCompletedSteps(detected);
      this.drawer.reachedStep = 4; // разрешаем переход на любой шаг
    } else {
      // Новая форма — начинаем с выбора типа
      this.drawer.legalTypeSelected = null;
      this.drawer.reachedStep = 0;
      this.drawer.completedSteps = new Set();
      this.form.resetForm();
    }
    // Всегда начинаем с шага 0 (выбор ЮЛ / ФЛ)
    this.drawer.currentStep = 0;
    this.form.clearErrors();
    this.drawer.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.drawer.isDrawerOpen = false;
    this._resetDrawerState();
  };

  private _resetDrawerState = () => {
    this.drawer.currentStep = 0;
    this.drawer.legalTypeSelected = null;
    this.dadata.reset();
    this.form.clearErrors();
  };

  // ── Форма ─────────────────────────────────────────────────────────────────

  setField = (field: keyof LegalFormData, value: string) => {
    this.form.setField(field, value);
  };

  resetForm = () => {
    this.form.resetForm();
  };

  selectLegalType = (type: "legal" | "selfemployed") => {
    this.drawer.legalTypeSelected = type;
    this.form.clearErrors();
  };

  /** Переход с шага 0 к шагу 1 (после выбора типа) */
  proceedFromTypeSelection = () => {
    if (!this.drawer.legalTypeSelected) return;
    this.drawer.currentStep = 1;
    if (this.drawer.reachedStep < 1) this.drawer.reachedStep = 1;
    this.form.clearErrors();
  };

  /** Переход на произвольный шаг (для таб-навигации).
   *  Если текущий шаг содержит несохранённые данные — показываем предупреждение.
   */
  goToStep = (step: number) => {
    if (step < 1 || step > 4) return;
    if (step === this.drawer.currentStep) return;
    const currentStep = this.drawer.currentStep;
    if (currentStep >= 1 && this.form.dirtySteps.has(currentStep)) {
      this.drawer.showUnsavedWarning(step);
      return;
    }
    this.drawer.currentStep = step;
    if (step > this.drawer.reachedStep) this.drawer.reachedStep = step;
    this.form.clearErrors();
  };

  /** true, когда все 4 шага сохранены на бэкенде. */
  isAllStepsCompleted = (): boolean => {
    const c = this.drawer.completedSteps;
    return c.has(1) && c.has(2) && c.has(3) && c.has(4);
  };

  /** Продолжить переход без сохранения (по кнопке "Продолжить без сохранения") */
  confirmUnsavedNavigate = () => {
    const target = this.drawer.unsavedWarning.targetStep;
    this.form.markStepClean(this.drawer.currentStep);
    this.drawer.dismissUnsavedWarning();
    this.drawer.currentStep = target;
    this.form.clearErrors();
  };

  /** Сохранить текущий шаг и перейти на целевой */
  saveAndNavigateToStep = async () => {
    const target = this.drawer.unsavedWarning.targetStep;
    try {
      await this._saveStepPartial();
      runInAction(() => {
        this.drawer.dismissUnsavedWarning();
        this.drawer.currentStep = target;
        this.form.clearErrors();
      });
    } catch {
      // ошибка уже показана toast'ом внутри _saveStepPartial
    }
  };

  // ── Навигация по шагам ────────────────────────────────────────────────────

  goBack = () => {
    if (this.drawer.currentStep > 0) {
      this.drawer.currentStep -= 1;
      this.form.clearErrors();
    }
  };

  goNext = async () => {
    if (this.submit.isSaving) return;
    if (this.drawer.legalTypeSelected === "selfemployed") {
      const ok = await this.form.validateSelfEmployed();
      if (!ok) return;
      await this._submitSelfEmployed();
      return;
    }
    if (this.drawer.legalTypeSelected === "legal") {
      const current = this.drawer.currentStep;
      const ok = await this.form.validateStep(current);
      if (!ok) return;

      // Если все шаги уже сохранены — сохраняем только если dirty, затем навигируем
      if (this.isAllStepsCompleted()) {
        try {
          await this._saveStepPartial();
          runInAction(() => {
            this.drawer.markStepCompleted(current);
            this.form.clearErrors();
          });
        } catch {
          // ошибка уже показана toast'ом
          return;
        }
        if (current < 4) {
          runInAction(() => {
            this.drawer.advanceTo(current + 1);
          });
        } else {
          runInAction(() => {
            this.status.isLegalDataFilled = true;
            this.drawer.isDrawerOpen = false;
            this.submit.showSuccessModal = true;
            this._resetDrawerState();
          });
        }
        return;
      }

      if (current < 4) {
        const stepToComplete = current;
        try {
          await this._saveStepPartial();
        } catch {
          return;
        }
        runInAction(() => {
          this.drawer.markStepCompleted(stepToComplete);
          this.drawer.advanceTo(stepToComplete + 1);
          this.form.clearErrors();
        });
      } else {
        await this._submitLegal();
      }
    }
  };

  // ── Частичное сохранение текущего шага ────────────────────────────────────

  private _detectCompletedSteps = (): number[] => {
    const f = this.form.form;
    const completed: number[] = [];
    if (f.legalName && f.inn && f.ogrn) completed.push(1);
    if (f.legalCountry && f.city) completed.push(2);
    if (f.directorFirstName && f.directorLastName) completed.push(3);
    if (f.bankName && f.bik && f.settlementAccount) completed.push(4);
    return completed;
  };

  private _sanitizeStreet = (s: string): string =>
    s
      .replace(/[^а-яёА-ЯЁa-zA-Z0-9 .,]/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();

  private _saveStepPartial = async () => {
    const step = this.drawer.currentStep;
    // Пропускаем, если шаг не менялся пользователем
    if (!this.form.dirtySteps.has(step)) return;

    const uuid = this.getUuid();
    if (!uuid) return;
    const f = this.form.form;
    const kpp = f.orgForm === "ИП" ? "000000000" : f.kpp || undefined;
    const rawStreet = f.building ? `${f.street}, ${f.building}` : f.street;
    const legalStreet = rawStreet ? this._sanitizeStreet(rawStreet) : undefined;

    this.submit.isSaving = true;
    try {
      await adminUpdateServiceCenterLegalData({
        serviceCenterUuid: uuid,
        requestBody: {
          legal_name: f.legalName || undefined,
          legal_full_name: f.legalFullName || undefined,
          inn: f.inn || undefined,
          ogrn: f.ogrn || undefined,
          kpp,
          orgForm: f.orgForm || undefined,
          billing_descriptor: f.billingDescriptor || undefined,
          legal_email: f.legalEmail || undefined,
          site_url: f.siteUrl || undefined,
          legal_country: f.legalCountry || undefined,
          legal_city: f.city || undefined,
          legal_street: legalStreet,
          legal_zip: f.postalCode || undefined,
          office: f.office || undefined,
          director_last_name: f.directorLastName || undefined,
          director_first_name: f.directorFirstName || undefined,
          director_middle_name: f.directorMiddleName || undefined,
          director_phone: f.directorPhone || undefined,
          director_citizenship: f.directorCitizenship || undefined,
          bank_name: f.bankName || undefined,
          bik: f.bik || undefined,
          correspondent_account: f.corrAccount || undefined,
          settlement_account: f.settlementAccount || undefined,
          bank_payment_details: f.bankPaymentDetails || undefined,
        },
      });
      runInAction(() => {
        this.submit.isSaving = false;
        this.status.hasLegalData = true;
        this.status.legalType = "legal";
        this.form.markStepClean(step);
        this.drawer.markStepCompleted(step);
      });
    } catch {
      runInAction(() => {
        this.submit.isSaving = false;
      });
      toastStore.showError("Не удалось сохранить данные шага");
      throw new Error("step_save_failed");
    }
  };

  // ── Применение подсказок DaData ───────────────────────────────────────────

  applyOrgSuggestion = (s: DadataSuggestion) => {
    const fields = this.dadata.extractOrgFields(s);
    const cur = this.form.form;
    this.form.setFormBatch({
      ...fields,
      directorLastName: fields.directorLastName || cur.directorLastName,
      directorFirstName: fields.directorFirstName || cur.directorFirstName,
      directorMiddleName: fields.directorMiddleName || cur.directorMiddleName,
    });
    this.dadata.setShowOrgSuggestions(false);
  };

  applyBankSuggestion = (s: DadataSuggestion) => {
    this.form.setFormBatch(this.dadata.extractBankFields(s));
    this.dadata.setShowBankSuggestions(false);
  };

  applyAddrSuggestion = (s: DadataSuggestion) => {
    const fields = this.dadata.extractAddrFields(s);
    const cur = this.form.form;
    this.form.setFormBatch({
      ...fields,
      office: fields.office || cur.office,
      postalCode: fields.postalCode || cur.postalCode,
    });
    this.dadata.setShowAddrSuggestions(false);
  };

  // ── Закрытие модалки успеха ───────────────────────────────────────────────

  closeSuccessModal = () => {
    this.submit.closeSuccessModal();
  };

  // ── Сабмит: юр. лицо ─────────────────────────────────────────────────────

  private _submitLegal = async () => {
    const uuid = this.getUuid();
    if (!uuid) return;
    const f = this.form.form;
    const kpp = f.orgForm === "ИП" ? "000000000" : f.kpp;
    const legalStreet = this._sanitizeStreet(
      f.building ? `${f.street}, ${f.building}` : f.street,
    );

    this.submit.isSaving = true;
    try {
      await adminUpdateServiceCenterLegalData({
        serviceCenterUuid: uuid,
        requestBody: {
          legal_name: f.legalName,
          legal_full_name: f.legalFullName,
          inn: f.inn,
          ogrn: f.ogrn,
          kpp,
          orgForm: f.orgForm,
          billing_descriptor: f.billingDescriptor,
          legal_email: f.legalEmail || undefined,
          site_url: f.siteUrl || undefined,
          legal_country: f.legalCountry,
          legal_city: f.city,
          legal_street: legalStreet,
          legal_zip: f.postalCode || undefined,
          office: f.office || undefined,
          director_last_name: f.directorLastName,
          director_first_name: f.directorFirstName,
          director_middle_name: f.directorMiddleName || undefined,
          director_phone: f.directorPhone || undefined,
          director_citizenship: f.directorCitizenship,
          bank_name: f.bankName,
          bik: f.bik,
          correspondent_account: f.corrAccount,
          settlement_account: f.settlementAccount,
          bank_payment_details: f.bankPaymentDetails || undefined,
        },
      });
      runInAction(() => {
        this.submit.isSaving = false;
        this.status.hasLegalData = true;
        this.status.legalType = "legal";
        this.status.rawLegalData = {
          legal_name: f.legalName,
          legal_full_name: f.legalFullName,
          inn: f.inn,
          ogrn: f.ogrn,
          kpp,
          orgForm: f.orgForm,
          billing_descriptor: f.billingDescriptor,
          legal_email: f.legalEmail,
          site_url: f.siteUrl,
          legal_country: f.legalCountry,
          legal_city: f.city,
          legal_street: legalStreet,
          legal_zip: f.postalCode,
          office: f.office,
          director_last_name: f.directorLastName,
          director_first_name: f.directorFirstName,
          director_middle_name: f.directorMiddleName,
          director_phone: f.directorPhone,
          director_citizenship: f.directorCitizenship,
          bank_name: f.bankName,
          bik: f.bik,
          correspondent_account: f.corrAccount,
          settlement_account: f.settlementAccount,
          bank_payment_details: f.bankPaymentDetails,
        };
        this.status.isLegalDataFilled = true;
        this.drawer.isDrawerOpen = false;
        this.submit.showSuccessModal = true;
        this._resetDrawerState();
      });
    } catch {
      runInAction(() => {
        this.submit.isSaving = false;
      });
      toastStore.showError("Не удалось сохранить юридические данные");
    }
  };

  // ── Сабмит: самозанятый ───────────────────────────────────────────────────

  private _submitSelfEmployed = async () => {
    const uuid = this.getUuid();
    if (!uuid) return;
    const f = this.form.form;

    this.submit.isSaving = true;
    try {
      await adminUpdateServiceCenterContractorData({
        serviceCenterUuid: uuid,
        requestBody: {
          contractor_full_name: f.selfFullName,
          contractor_inn: f.selfInn,
          contractor_phone: f.selfPhone || undefined,
          contractor_passport_series: f.selfPassportSeries || undefined,
          contractor_passport_number: f.selfPassportNumber || undefined,
          contractor_passport_issued_by: f.selfPassportIssuedBy || undefined,
          contractor_passport_issue_date: f.selfPassportDate || undefined,
        },
      });
      runInAction(() => {
        this.submit.isSaving = false;
        this.status.hasLegalData = true;
        this.status.legalType = "selfemployed";
        this.status.rawContractorData = {
          contractor_full_name: f.selfFullName,
          contractor_inn: f.selfInn,
          contractor_phone: f.selfPhone,
          contractor_passport_series: f.selfPassportSeries,
          contractor_passport_number: f.selfPassportNumber,
          contractor_passport_issued_by: f.selfPassportIssuedBy,
          contractor_passport_issue_date: f.selfPassportDate,
        };
        this.status.isLegalDataFilled = true;
        this.drawer.isDrawerOpen = false;
        this.submit.showSuccessModal = true;
        this._resetDrawerState();
      });
    } catch {
      runInAction(() => {
        this.submit.isSaving = false;
      });
      toastStore.showError("Не удалось сохранить данные самозанятого");
    }
  };

  // ── Сброс всего состояния ─────────────────────────────────────────────────

  reset = () => {
    this.status.reset();
    this.form.resetForm();
    this.dadata.reset();
    this.submit.reset();
    this.drawer.reset();
  };
}
